import { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { Pressable, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  ReduceMotion,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { styles } from './Toast.styles';

export interface IToastProps {
  type: 'success' | 'error' | 'warning';
  icon?: ReactNode;
  message: ReactNode;
  action?: {
    label: string;
    method: () => void;
  };
  closable?: boolean;
  onClose?: () => void;
  removing?: boolean;
}

export const Toast = ({
  type,
  icon,
  message,
  action,
  closable = false,
  onClose = () => {},
  removing = false,
}: IToastProps) => {
  const currentBreakpoint = useBreakpoints(['md']);

  const translateX = useSharedValue(currentBreakpoint.md ? 500 : 0);
  const translateY = useSharedValue(currentBreakpoint.md ? 0 : -200);
  const isActive = useSharedValue(true);

  const handleClose = useCallback(() => {
    if (!isActive.value) return;
    isActive.value = false;

    if (currentBreakpoint.md) {
      translateX.value = withTiming(
        500,
        {
          duration: 450,
          easing: Easing.out(Easing.quad),
          reduceMotion: ReduceMotion.System,
        },
        () => {
          runOnJS(onClose)();
        }
      );
    } else {
      translateY.value = withTiming(
        -300,
        {
          duration: 650,
          easing: Easing.out(Easing.quad),
          reduceMotion: ReduceMotion.System,
        },
        () => {
          runOnJS(onClose)();
        }
      );
    }
  }, [currentBreakpoint.md, translateX, translateY, onClose, isActive]);

  const gesture = Gesture.Pan()
    .onUpdate((event) => {
      if (!isActive.value) return;

      if (currentBreakpoint.md && event.translationX > 0) {
        translateX.value = event.translationX;
      } else if (!currentBreakpoint.md && event.translationY < 0) {
        translateY.value = event.translationY;
      }
    })
    .onEnd((event) => {
      if (!isActive.value) return;

      const threshold = currentBreakpoint.md ? 100 : -50;
      const shouldDismiss = currentBreakpoint.md
        ? event.translationX > threshold
        : event.translationY < threshold;

      if (shouldDismiss) {
        runOnJS(handleClose)();
      } else {
        if (currentBreakpoint.md) {
          translateX.value = withTiming(0);
        } else {
          translateY.value = withTiming(0);
        }
      }
    });

  useEffect(() => {
    if (removing) {
      handleClose();
    } else if (!removing) {
      if (currentBreakpoint.md) {
        translateX.value = withTiming(0, {
          duration: 600,
          easing: Easing.out(Easing.quad),
          reduceMotion: ReduceMotion.System,
        });
      } else {
        translateY.value = withTiming(1, {
          duration: 600,
          easing: Easing.out(Easing.quad),
          reduceMotion: ReduceMotion.System,
        });
      }
    }
  }, [removing, handleClose, translateX, currentBreakpoint.md, translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
    ],
  }));

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View style={animatedStyle}>
        <Stack
          horizontal
          gap="lg"
          justify="between"
          align="center"
          className={styles.host({
            type,
          })}
        >
          <Stack gap="md" justify="center" horizontal grow>
            <View>{icon ?? getIcon(type)}</View>
            <Stack align="center" horizontal grow>
              <BodyText className={styles.content}>{message}</BodyText>
              {action && (
                <Button variant="secondary" size="sm" onPress={action.method}>
                  {action.label}
                </Button>
              )}
            </Stack>
          </Stack>
          {closable && (
            <Pressable
              onPress={() => {
                handleClose();
              }}
            >
              <Icon icon="xmark" />
            </Pressable>
          )}
        </Stack>
      </Animated.View>
    </GestureDetector>
  );
};

const getIcon = (type: IToastProps['type']): ReactElement => {
  switch (type) {
    case 'success':
      return (
        <IconBadge
          icon={<Icon icon={'check'} color="neutral10" />}
          ratio={32}
          backgroundColor="systemSuccess20"
        />
      );
    case 'warning':
      return (
        <IconBadge
          icon={<Icon icon={'exclamation'} color="neutral10" />}
          ratio={32}
          backgroundColor="systemWarning20"
        />
      );
    case 'error':
      return (
        <IconBadge
          icon={<Icon icon={'xmark'} color="neutral10" />}
          ratio={32}
          backgroundColor="systemError20"
        />
      );
  }
};
